import { Fragment, useEffect, useReducer } from 'react'
import {
  ChatAltIcon,
  TagIcon,
  UserCircleIcon,
  PencilAltIcon,
  BriefcaseIcon,
} from '@heroicons/react/solid'
import classNames from 'classnames'
import { Disclosure } from '@headlessui/react'
import { useForm, useFieldArray } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'

type Experience = {
  title: string
  company: string
  industry: string
  start_date: string
  end_date: string
  summary: string
  current: boolean
}

type State = {
  experience: Experience[]
}

const initialState = {
  experience: [],
}

function reducer(state: State, action: { type: string; payload?: any }) {
  if (action.type === 'add') {
    return {
      ...state,
      experience: [
        ...state.experience,
        {
          title: '',
          company: '',
          industry: '',
          start_date: '',
          end_date: '',
          summary: '',
          current: false,
        },
      ],
    }
  }
  if (action.type === 'update') {
    return {
      ...state,
      experience: state.experience.map((entry, index) => {
        if (index !== action.payload.i) {
          return entry
        }

        return {
          ...action.payload.values,
          current: action.payload.values.current === 'on',
        }
      }),
    }
  }
  if (action.type === 'delete') {
    return {
      ...state,
      experience: [
        ...state.experience.slice(0, action.payload),
        ...state.experience.slice(action.payload + 1),
      ],
    }
  }
  return state
}

const schema = z.object({
  experience: z.array(
    z
      .object({
        title: z.string().min(1, 'This field is required'),
        company: z.string().optional(),
        industry: z.string().optional(),
        start_date: z.preprocess(
          (val: unknown) => (!val || (val as string).length === 0 ? null : val),
          z
            .string()
            .regex(
              /[0-9]{4}-[0-9]{2}-[0-9]{2}/i,
              'Invalid format, please use YYYY-MM-DD'
            )
            .nullish()
        ),
        end_date: z.preprocess(
          (val: unknown) => (!val || (val as string).length === 0 ? null : val),
          z
            .string()
            .regex(
              /[0-9]{4}-[0-9]{2}-[0-9]{2}/i,
              'Invalid format, please use YYYY-MM-DD'
            )
            .nullish()
        ),
        current: z.boolean(),
        summary: z.string().optional(),
      })
      .superRefine((arg, ctx) => {
        if (arg.start_date && !arg.current && !arg.end_date) {
          ctx.addIssue({
            path: ['end_date'],
            message: 'Provide end date or select the checkbox below',
            code: 'custom',
          })
        }
      })
  ),
})

export function ExperienceFormInputWorkable() {
  const [state, dispatch] = useReducer(reducer, initialState)

  const {
    control,
    register,
    trigger,
    formState: { errors, isValid },
    getValues,
  } = useForm<{
    experience: Experience[]
  }>({
    resolver: zodResolver(schema),
  })
  const { fields, append, prepend, remove, swap, move, insert } = useFieldArray(
    {
      control,
      name: 'experience',
    }
  )

  const onSubmit = async (i: number, close: () => void) => {
    const validationResult = await trigger()

    if (validationResult) {
      dispatch({
        type: 'update',
        payload: { i, values: getValues(`experience.${i}`) },
      })

      close()
    }
  }

  return (
    <div>
      <div className="-my-5 mt-6 flow-root divide-y divide-gray-200">
        {state.experience.map((entry, i) => (
          <Disclosure key={i} as={'div'} className="" defaultOpen>
            <div className="py-4">
              <div className="flex items-center space-x-4">
                <div className="flex-shrink-0">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-100">
                    <BriefcaseIcon className="h-4 w-4 text-gray-400" />
                  </div>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-gray-900">
                    {entry.title}
                  </p>
                  <p className="truncate text-sm text-gray-500">
                    {entry.company}
                  </p>
                </div>
                <div>
                  <Disclosure.Button className="inline-flex items-center rounded-full border border-gray-300 bg-white px-2.5 py-0.5 text-sm font-medium leading-5 text-gray-700 shadow-sm hover:bg-gray-50">
                    {({ open: isOpen }: { open: boolean }) => (
                      <>{isOpen ? 'cancel' : 'edit'}</>
                    )}
                  </Disclosure.Button>
                </div>
              </div>
            </div>

            <Disclosure.Panel unmount={false}>
              {({ close }) => (
                <div className="p-5">
                  <div className="">
                    <label className="block text-sm font-medium text-gray-700">
                      Title<span className="text-rose-500"> *</span>
                    </label>
                    <div className="mt-1">
                      <input
                        {...register(`experience.${i}.title` as const)}
                        type="text"
                        autoComplete="off"
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-accent-color focus:ring-accent-color sm:text-sm"
                      />
                      {errors?.experience && errors.experience[i]?.title && (
                        <div className="pt-1 text-red-700" id="password-error">
                          {errors?.experience[i]?.title?.message}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700">
                      Company
                    </label>
                    <div className="mt-1">
                      <input
                        {...register(`experience.${i}.company` as const)}
                        type="text"
                        autoComplete="off"
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-accent-color focus:ring-accent-color sm:text-sm"
                      />
                      {errors?.experience && errors.experience[i]?.company && (
                        <div className="pt-1 text-red-700" id="password-error">
                          {errors?.experience[i]?.company?.message}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700">
                      Industry
                    </label>
                    <div className="mt-1">
                      <input
                        {...register(`experience.${i}.industry` as const)}
                        type="text"
                        autoComplete="off"
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-accent-color focus:ring-accent-color sm:text-sm"
                      />
                      {errors?.experience && errors.experience[i]?.industry && (
                        <div className="pt-1 text-red-700" id="password-error">
                          {errors?.experience[i]?.industry?.message}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700">
                      Summary
                    </label>
                    <div className="mt-1">
                      <textarea
                        {...register(`experience.${i}.summary` as const)}
                        rows={3}
                        className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-accent-color focus:ring-accent-color sm:text-sm"
                        placeholder=""
                        defaultValue={''}
                      />
                      {errors?.experience && errors.experience[i]?.summary && (
                        <div className="pt-1 text-red-700" id="password-error">
                          {errors?.experience[i]?.summary?.message}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex">
                    <div className="mt-4 w-1/2 pr-4">
                      <label className="block text-sm font-medium text-gray-700">
                        Start date
                      </label>
                      <div className="mt-1">
                        <input
                          {...register(`experience.${i}.start_date` as const)}
                          type="text"
                          autoComplete="off"
                          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-accent-color focus:ring-accent-color sm:text-sm"
                        />
                        <p
                          className="mt-1 text-xs text-gray-500"
                          id="email-description"
                        >
                          YYYY-MM-DD
                        </p>
                        {errors?.experience &&
                          errors.experience[i]?.start_date && (
                            <div
                              className="pt-1 text-red-700"
                              id="password-error"
                            >
                              {errors?.experience[i]?.start_date?.message}
                            </div>
                          )}
                      </div>
                    </div>
                    <div className="mt-4 w-1/2 pl-4">
                      <label className="block text-sm font-medium text-gray-700">
                        End date
                      </label>
                      <div className="mt-1">
                        <input
                          {...register(`experience.${i}.end_date` as const)}
                          type="text"
                          autoComplete="off"
                          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-accent-color focus:ring-accent-color sm:text-sm"
                        />
                        <p
                          className="mt-1 text-xs text-gray-500"
                          id="email-description"
                        >
                          YYYY-MM-DD
                        </p>
                        {errors?.experience && errors.experience[i]?.end_date && (
                          <div
                            className="pt-1 text-red-700"
                            id="password-error"
                          >
                            {errors?.experience[i]?.end_date?.message}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="relative mt-4 flex items-start">
                    <div className="flex h-5 items-center">
                      <input
                        {...register(`experience.${i}.current`)}
                        type="checkbox"
                        className="h-4 w-4 rounded border-gray-300 text-accent-color focus:ring-accent-color"
                      />
                    </div>
                    <div className="ml-3 text-sm">
                      <label
                        htmlFor="comments"
                        className="font-normal text-gray-700"
                      >
                        I currently work here
                      </label>
                    </div>
                  </div>
                  <div className="mt-4 flex justify-end">
                    <button
                      onClick={(e) => {
                        e.preventDefault()
                        dispatch({ type: 'delete', payload: i })
                        remove(i)
                      }}
                      className='className="inline-flex items-center rounded-full border border-rose-300 bg-white px-2.5 py-0.5 text-sm font-medium leading-5 text-rose-400 shadow-sm hover:border-rose-500'
                    >
                      delete
                    </button>
                    <button
                      onClick={(event) => {
                        event.preventDefault()
                        onSubmit(i, close)
                      }}
                      className="ml-2 inline-flex items-center rounded-full border border-gray-300 bg-white px-2.5 py-0.5 text-sm font-medium leading-5 text-gray-700 shadow-sm hover:bg-gray-50"
                    >
                      Save
                    </button>
                  </div>
                </div>
              )}
            </Disclosure.Panel>
          </Disclosure>
        ))}
      </div>
      <div className="mt-6">
        <button
          onClick={(e) => {
            e.preventDefault()
            dispatch({ type: 'add' })
            append({})
          }}
          className="mx-auto flex w-96 items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
        >
          Add position
        </button>
      </div>
    </div>
  )
}
